import * as React from "react";

import { appendCurrentLocationAsFrom, employeeProfileHref } from "@performyard/utils-isomorphic";

import { ButtonStandard } from "../../atoms/ButtonStandard";
import { Link } from "../../atoms/Link";
import { Text } from "../../atoms/Text";
import { Avatar } from "../Avatar";
import { ModalBase } from "../ModalBase";
import type { AvatarGroupEmployee } from "./index";

export type AvatarGroupModalProps = {
	employees: AvatarGroupEmployee[];
	isOpen: boolean;
	onClose: () => void;
	title?: string;
	subtitle?: string;
	hasLinks?: boolean;
};

/**
 * Modal listing all employees in an avatar group.
 */
export const AvatarGroupModal = ({
	employees,
	isOpen,
	onClose,
	title = "Employees",
	subtitle,
	hasLinks = false,
}: AvatarGroupModalProps) => {
	return (
		<ModalBase isOpen={isOpen} onClose={onClose} dangerouslySetClassName="w-[432px]">
			<div className="px-6 pb-4 text-center">
				<Text size="sub-heading" weight="semi-bold">
					{title}
				</Text>
				{subtitle && (
					<Text size="paragraph-2" weight="regular">
						{subtitle}
					</Text>
				)}
			</div>

			<div className="max-h-80 overflow-y-auto px-6" data-testid="avatar-group-modal-list">
				{employees.map((employee, index) => (
					<div
						key={`${employee._id}-${index}`}
						className={`flex items-center gap-2 px-2 py-1 ${index % 2 === 0 ? "bg-neutral-1" : ""}`}
					>
						<span className="w-7 shrink-0 text-paragraph-2 text-neutral-7">{index + 1}.</span>
						<Avatar size={20} id={employee._id} name={employee.name} />
						{hasLinks ? (
							<Link
								href={appendCurrentLocationAsFrom(employeeProfileHref(employee._id))}
								forceHardReload
								underline="never"
							>
								<span className="py-line-clamp-1 text-paragraph-3">{employee.name}</span>
							</Link>
						) : (
							<span className="py-line-clamp-1 text-paragraph-3 text-neutral-9">{employee.name}</span>
						)}
					</div>
				))}
			</div>

			<div className="flex justify-center p-6">
				<ButtonStandard color="primary" size="large" onClick={onClose} dangerouslySetClassName="w-fit">
					Close
				</ButtonStandard>
			</div>
		</ModalBase>
	);
};

AvatarGroupModal.displayName = "AvatarGroupModal";
